import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import type { ShipmentTransportProfileView } from '../../api/generated/models/ShipmentTransportProfileView';

interface TransportProfileModalProps {
  mode: 'create' | 'edit';
  initial?: ShipmentTransportProfileView | null;
  onClose: () => void;
  onRequestClose: () => void;
  onSave: (payload: { name: string; details: string | null }) => void;
  saving?: boolean;
  errorMessage?: string | null;
}

const TransportProfileModal: React.FC<TransportProfileModalProps> = ({
  mode,
  initial,
  onClose,
  onRequestClose,
  onSave,
  saving = false,
  errorMessage = null,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.mode ?? '');
  const [details, setDetails] = useState(initial?.details ?? '');

  useEffect(() => {
    setName(initial?.mode ?? '');
    setDetails(initial?.details ?? '');
  }, [initial]);

  const dirty =
    name.trim() !== (initial?.mode ?? '').trim() ||
    details.trim() !== (initial?.details ?? '').trim();

  const handleClose = () => {
    if (dirty) {
      onRequestClose();
      return;
    }
    onClose();
  };

  const title =
    mode === 'create'
      ? t('shipment_transport_modal_create_title')
      : t('shipment_transport_modal_edit_title');

  const intro =
    mode === 'create'
      ? t('shipment_transport_modal_create_intro')
      : t('shipment_transport_modal_edit_intro');

  return (
    <Modal isOpen onClose={handleClose} title={title} className="shipment-tracker-modal">
      <p className="shipment-tracker-modal__intro">{intro}</p>
      <label className="form-label">
        {t('shipment_transport_field_name')} *
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('shipment_transport_field_name_placeholder')}
        />
      </label>
      <p className="form-hint">{t('shipment_transport_field_name_hint')}</p>
      {errorMessage && <p className="form-error">{errorMessage}</p>}
      <label className="form-label">
        {t('shipment_transport_field_details')}
        <input
          className="input"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={t('shipment_transport_field_details_placeholder')}
        />
      </label>
      <p className="form-hint">{t('shipment_transport_field_details_hint')}</p>
      <div className="shipment-tracker-modal__footer">
        <button
          type="button"
          className="shipment-tracker-modal__btn shipment-tracker-modal__btn--secondary"
          onClick={handleClose}
          disabled={saving}
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          className={`shipment-tracker-modal__btn shipment-tracker-modal__btn--primary${
            mode === 'create' ? ' shipment-tracker-modal__btn--primary-with-icon' : ''
          }`}
          disabled={saving || !name.trim()}
          onClick={() =>
            onSave({
              name: name.trim(),
              details: details.trim() === '' ? null : details.trim(),
            })
          }
        >
          {mode === 'create' && <PlusIcon aria-hidden width={16} height={16} />}
          {mode === 'create' ? t('shipment_transport_create_btn') : t('save')}
        </button>
      </div>
    </Modal>
  );
};

export default TransportProfileModal;
